/**
 * Finance widget controller (Novuna + Clearpay)
 * -------------------------------------------------
 * Watches the product / cart / price-list areas and, when the finance
 * app injects its markup, rebuilds it into the custom Novuna widget,
 * filters which providers show, relocates the finance message, and
 * styles the Clearpay shadow-DOM placement.
 *
 * Structure:
 *   CONFIG            – all selectors, thresholds and page settings in one place
 *   utils            – small pure helpers
 *   injectStyles     – one-time <style> injection
 *   filterProviders  – decides which finance blocks are shown / ordered
 *   buildNovunaWidget – rebuilds the app markup into the custom widget + modal
 *   relocateMessage  – moves & restyles the "Finance available" line
 *   styleShadowP     – styles the <p> inside the Clearpay shadow root (safe)
 *   handleProduct / handleCart / handleAdditional – thin observer callbacks
 *   ObserverManager  – registers / disconnects observers
 *   init             – wires everything up by pathname
 *
 * To add a new page or provider, edit CONFIG only — the callbacks read from it.
 */
(function () {
  'use strict';

  /* ============================================================
   * CONFIG — the only place you should need to touch for tweaks
   * ========================================================== */
  const CONFIG = {
    // Roots we attach MutationObservers to
    observeTargets: {
      productForm: 'div.product-form',
      cartAside: 'div.page-content.page-content--fluid div.cart__aside',
      priceList: 'div.price-list[data-product-price-list]',
    },

    // Shared selectors used across the app
    shared: {
      popupId: 'PBFPopup',
      nonApplicableId: 'PBFNonApplicable',
      messageSource: 'div#pbf-finance-avail-from',
      novunaBlock: 'div.shopify-block > div.new-novuna-block',
      saveButtonId: 'pbf-save-button',
      invertedIconsId: 'inverted-icons',
      modalId: 'novuna-modal',
      modalContent: '.novuna-modal-content',
      modalClose: 'novuna-modal-close',
      styleTagId: 'novuna-widget-styles',
    },

    thresholds: {
      // Price bands (inclusive upper bounds):
      //   £1 – £500          -> Clearpay only
      //   over £500 – £1,200 -> Clearpay + Novuna
      //   over £1,200        -> Novuna only
      novunaMin: 500,    // Novuna shows when amount >  this
      clearpayMax: 1200, // Clearpay shows when amount <= this
    },

    // Per-page settings. Add another entry here to support a new page.
    pages: {
      product: {
        isCart: false,
        migrationBox: 'div#financing-migration-box',
        clearpaySelector: 'div.shopify-block > square-placement',
        // clearpayFlagAttr: 'data-clearpayfilterapplicableforprodpage',
        totalAttribute: 'data-prodTotal',
        messageTarget: 'div#novuna-finance-avail-from',
        messagePadding: '0 0 10px',
        floatViewPlans: false,
        appliesOnPath: (path) => path !== '/cart',
      },
      cart: {
        isCart: true,
        migrationBox: 'div#financing-migration-box-cart',
        clearpaySelector: 'form+square-placement',
        // clearpayFlagAttr: 'data-clearpayfilterapplicableforprodpage',
        totalAttribute: 'data-cartTotal',
        messageTarget: 'div#novuna-finance-avail-from-cart',
        messagePadding: '0 0 5px',
        floatViewPlans: true,
        appliesOnPath: (path) => path === '/cart',
        shadowHost: 'div#financing-migration-box-cart square-placement',
        pbfWrapperStyle: { fontSize: '0.9rem' },
      },
    },
  };

  const WIDGET_CSS = `
    .new-novuna-block {
      display: flex;
      flex-direction: row-reverse;
      gap: 10px;
      justify-content: start;
      align-items: center;
      margin-top: -10px;
    }
    .new-novuna-block--cart {
      flex-direction: row-reverse;
      align-items: center;
    }
    .new-novuna-block--cart .spread-cost-msg {
      margin-left: 0 !important;
    }
    @media (max-width: 749px) {
      .spread-cost-msg {
        margin-left: 0 !important;
      }
    }
    .widget-provider-separator {
      border-bottom: 1px solid #eee;
      margin-bottom: 10px !important;
    }
  `;

  /* ============================================================
   * UTILITIES
   * ========================================================== */
  const utils = {
    // Pull the first numeric value out of an attribute string -> float
    parseAmount(str) {
      if (!str) return NaN;
      const match = str.match(/[0-9,.]+/g);
      if (!match) return NaN;
      return parseFloat(match[0].replace(/[^\d.]/g, ''));
    },

    // Inject a <style> once, keyed by id
    injectStylesOnce(id, css) {
      if (document.getElementById(id)) return;
      const style = document.createElement('style');
      style.id = id;
      style.textContent = css;
      document.head.appendChild(style);
    },

    // Apply a set of inline styles from an object
    applyStyles(el, styles) {
      if (el) Object.assign(el.style, styles);
    },
  };

  /* ============================================================
   * PROVIDER FILTERING
   * Decides which finance blocks show, and in what order.
   * ========================================================== */
  function filterProviders({
    clearpaySelector,
    novunaSelector,
    forCart,
    containerBox,
    totalAttribute,
  }) {
    const amount = utils.parseAmount(containerBox.getAttribute(totalAttribute));

    // Which providers this price band allows:
    //   £1 – £500          -> Clearpay only
    //   over £500 – £1,200 -> Clearpay + Novuna
    //   over £1,200        -> Novuna only
    const showClearpay = amount <= CONFIG.thresholds.clearpayMax;
    const showNovuna   = amount >  CONFIG.thresholds.novunaMin;

    let providers = [];
    let alternateMessage = null;

    // --- Clearpay ---
    const clearPayDiv = forCart
      ? document.querySelector(clearpaySelector)
      : document.querySelector(clearpaySelector)?.parentNode;

    if (clearPayDiv) {
      if (showClearpay) {
        // Add spacing for clearpay if both widgets are visible
        if(showClearpay) {
          clearPayDiv.style.paddingBottom = '14px';
        }
        providers.push(clearPayDiv);
      } else {
        clearPayDiv.style.display = 'none';
      }
    }

    // --- Novuna ---
    const novunaPayDiv = document.querySelector(novunaSelector)?.parentNode;
    if (novunaPayDiv) {
      if (showNovuna) {
        const saveButton = document.getElementById(CONFIG.shared.saveButtonId);
        if (saveButton) saveButton.innerText = 'Pay by Finance';
        providers.push(novunaPayDiv);
      } else {
        novunaPayDiv.style.display = 'none';
      }
    }

    if (providers.length === 0) return;

    // When both show, Novuna leads (reverse [clearpay, novuna] -> [novuna, clearpay])
    if (showClearpay && showNovuna) {
      providers = providers.reverse();
      // Add CSS class to both providers
      for(let p of providers) {
        p.classList.add('widget-provider-separator');
      }
    }

    // Clearpay-only band: nudge toward Novuna
    // if (amount <= CONFIG.thresholds.novunaMin) {
    //   alternateMessage = document.createElement('p');
    //   alternateMessage.innerHTML =
    //     "Spend over £500 for finance options from " +
    //     "<strong style='color: #7e3cfd;'>Novuna</strong>, spread across 12, 18, 36 or 48 months";
    //   alternateMessage.style.marginTop = '7px';
    //   containerBox.style.paddingBottom = '10px';
    // }

    // Re-mount providers into the container (append moves nodes, preserving listeners)
    containerBox.innerHTML = '';
    // if (amount >= CONFIG.thresholds.novunaMin && !forCart) {
      // providers[0].style.marginTop = '16px';
    // }
    if (providers[1] && !forCart) {
      providers[1].style.marginTop = '0';
      providers[1].style.marginBottom = '10px';
    }
    if (providers[1] && forCart) {
      providers[1].style.marginTop = '10px';
    }
    containerBox.append(...providers);

    // if (alternateMessage && showNovuna) containerBox.append(alternateMessage);
  }

  /* ============================================================
   * NOVUNA WIDGET BUILDER
   * Rebuilds the app's injected markup into the custom widget,
   * moves the detail content into the modal, and wires up open/close.
   * Idempotent: bails if already built.
   * ========================================================== */
  function buildNovunaWidget(section, isCart) {
    if (section.querySelector('.new-novuna-block')) return; // already built

    const blockId = section.getAttribute('id');
    const root = `div#${blockId} div#${CONFIG.shared.popupId}`;

    const wrapper = document.querySelector(root);
    const image = document.querySelector(`${root} img`);
    const messageText = document.querySelector(`${root} div.pbf-finance-widget div#pbf-finance-avail-from`);
    const button = document.querySelector(`${root} div.pbf-finance-widget div.pbf-finance-widget-button`);
    const description = document.querySelector(`${root} div.pbf-representative-example`);
    const form = document.querySelector(`${root} div.pbf-finance-widget-calculator`);

    if (!wrapper || !image || !messageText || !button || !form) return;

    const modal = document.getElementById(CONFIG.shared.modalId);
    if (!modal) return;
    const modalContent = modal.querySelector(CONFIG.shared.modalContent);
    if (!modalContent) return;

    // --- Modal show / hide handlers ---
    const showModal = () => {
      utils.applyStyles(modal, {
        visibility: 'visible',
        transform: 'scale(1)',
        opacity: 1,
        transition: 'visibility 0s linear 0s,opacity 0.25s 0s,transform 0.25s',
      });
    };
    const hideModal = () => {
      utils.applyStyles(modal, {
        transform: 'scale(1.1)',
        opacity: 0,
        visibility: 'hidden',
        transition: 'visibility 0s linear 0.25s,opacity 0.25s 0s,transform 0.25s',
      });
    };

    // Lay out the popup wrapper
    utils.applyStyles(wrapper, {
      maxWidth: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'start',
    });

    button.style.display = 'none';

    // Move popup content into the modal
    modalContent.append(...section.childNodes);

    const saveButton = form.querySelector(`button#${CONFIG.shared.saveButtonId}`);
    if (saveButton) saveButton.classList.add('center-pbf-button');

    const closeBtn = document.getElementsByClassName(CONFIG.shared.modalClose)[0];
    if (closeBtn) closeBtn.addEventListener('click', hideModal);

    // Clone the finance logo into the modal, then style the live one
    const imageClone = image.cloneNode(true);
    modalContent.prepend(imageClone);
    if (description) modalContent.append(description);

    utils.applyStyles(image, {
      width: 'var(--logo-badge-width, 130px)',
      aspectRatio: '3/1',
      objectFit: 'contain',
    });
    if (!isCart) {
      utils.applyStyles(image, {
        minWidth: '140px',
        marginLeft: '-5px',
        marginTop: '3px',
      });
    }

    const inverted = document.getElementById(CONFIG.shared.invertedIconsId);
    if (inverted?.value === 'true') {
      image.style.filter = 'brightness(0) invert(1)';
    }

    // Clickable image wrapper -> opens modal
    const imgWrapper = document.createElement('div');
    utils.applyStyles(imgWrapper, {
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    });
    imgWrapper.appendChild(image);
    imgWrapper.onclick = showModal;

    // "View Plans" button -> opens modal
    const viewPlans = document.createElement('span');
    viewPlans.textContent = 'View Plans';
    utils.applyStyles(viewPlans, {
      color: '#7e3cfd',
      display: 'inline-block',
      cursor: 'pointer',
    });
    viewPlans.onclick = showModal;

    // Assemble the new widget
    const widget = document.createElement('div');
    widget.classList.add('new-novuna-block');
    if (isCart) widget.classList.add('new-novuna-block--cart');
    widget.appendChild(imgWrapper);

    const message = document.createElement('span');
    message.classList.add('spread-cost-msg');
    utils.applyStyles(message, {
      fontSize: '0.85rem',
      fontWeight: '600',
      marginBottom: '0.5rem',
    });
    message.textContent = '0% finance available with';
    widget.appendChild(message);

    utils.applyStyles(messageText, { padding: '2px 10px', lineHeight: '20px' });
    messageText.appendChild(viewPlans);
    widget.appendChild(messageText);

    section.appendChild(widget);
  }

  /* ============================================================
   * MESSAGE RELOCATION
   * Moves the "Finance available" line into the target container
   * and restyles it — editing text nodes in place so the
   * "View Plans" click handler survives (no innerHTML rewrite).
   * ========================================================== */
  function relocateMessage(page) {
    const target = document.querySelector(page.messageTarget);
    const source = document.querySelector(CONFIG.shared.messageSource);
    if (!target || !source || !page.appliesOnPath(window.location.pathname)) return;

    utils.applyStyles(source, { padding: page.messagePadding, fontWeight: '600', ...page.pbfWrapperStyle });

    source.childNodes.forEach((node) => {
      // Plain text: swap the label
      if (node.nodeType === Node.TEXT_NODE && node.textContent.includes('Finance available')) {
        node.textContent = node.textContent.replace('Finance available', 'or');
      }
      // Element (the "View Plans" span): restyle — guard nodeType so text nodes never hit .style
      if (node.nodeType === Node.ELEMENT_NODE && node.textContent.includes('View Plans')) {
        utils.applyStyles(node, {
          marginLeft: '10px',
          textDecoration: 'underline',
          fontWeight: '500',
        });
        if (page.floatViewPlans) node.style.float = 'right';

        // If inverted
        const inverted = document.getElementById(CONFIG.shared.invertedIconsId);
        if (inverted?.value === 'true') {
          node.style.color = 'white';
        }
      }
    });

    target.appendChild(source); // moves node -> listeners intact
  }

  /* ============================================================
  * CLEARPAY BACKUP ROUTING
  * When Novuna isn't available (its message container is empty,
  * or the pbf message is hidden), move the Clearpay block into
  * #clearpay-finance-backup instead of leaving it alongside
  * Novuna in the shared financing-migration-box.
  * Self-correcting: when Novuna returns, filterProviders re-mounts
  * Clearpay into the box and the backup is hidden again.
  * ========================================================== */
  function isNovunaUnavailable(page) {
    const novunaTarget = document.querySelector(page.messageTarget);      // novuna-finance-avail-from(-cart)
    const pbfMsg = document.querySelector(CONFIG.shared.messageSource);   // pbf-finance-avail-from

    const pbfHidden = !pbfMsg || window.getComputedStyle(pbfMsg).display === 'none';
    const novunaEmpty = !novunaTarget
      || novunaTarget.textContent.trim() === ''
      || window.getComputedStyle(novunaTarget).display === 'none';

    return pbfHidden || novunaEmpty;
  }

  /* ============================================================
   * SHADOW ROOT STYLING (Clearpay placement)
   * Safe against missing host / closed-or-unready shadow root:
   * returns false and the observer simply retries next mutation.
   * ========================================================== */
  function styleShadowP(hostSelector, styles) {
    const p = document.querySelector(hostSelector)?.shadowRoot?.querySelector('p');
    // console.log('Clearpay placement shadow root!');
    if (!p) return false;                       // host / shadow root not ready — retry next mutation
    if (p.dataset.novunaStyled === '1') return true; // already done — true no-op
    utils.applyStyles(p, styles);
    p.dataset.novunaStyled = '1';
    return true;
  }

  /* ============================================================
   * SHARED STEPS
   * ========================================================== */
  function getNovunaSection() {
    return document.getElementById(CONFIG.shared.popupId)?.parentElement || null;
  }

  function hideNonApplicable() {
    const block = document.getElementById(CONFIG.shared.nonApplicableId)?.parentElement;
    if (block) block.style.display = 'none';
  }

  /* ============================================================
   * OBSERVER CALLBACKS (thin — delegate to shared steps)
   * ========================================================== */
  function handleProduct() {
    const page = CONFIG.pages.product;

    const section = getNovunaSection();
    if (section) buildNovunaWidget(section, page.isCart);

    hideNonApplicable();

    const box = document.querySelector(page.migrationBox);
    if (box && window.location.pathname !== '/cart') {
      filterProviders({
        clearpaySelector: page.clearpaySelector,
        novunaSelector: CONFIG.shared.novunaBlock,
        forCart: page.isCart,
        containerBox: box,
        totalAttribute: page.totalAttribute,
      });
    }

    relocateMessage(page);
    // routeClearpayBackup(page); 
  }

  function handleCart() {
    const page = CONFIG.pages.cart;

    const section = getNovunaSection();
    const nonApplicable = document.getElementById(CONFIG.shared.nonApplicableId)?.parentElement;
    if (nonApplicable) nonApplicable.style.display = 'none';

    const injectedClearpay = document.querySelector('form+square-placement');
    if ((!section && !nonApplicable) || !injectedClearpay) return;

    if (section) buildNovunaWidget(section, page.isCart);

    // Cart-specific: normalise the Clearpay placement alignment
    utils.applyStyles(injectedClearpay, { textAlign: 'left', margin: '0' });

    const box = document.querySelector(page.migrationBox);
    if (box) {
      filterProviders({
        clearpaySelector: page.clearpaySelector,
        novunaSelector: CONFIG.shared.novunaBlock,
        forCart: page.isCart,
        containerBox: box,
        totalAttribute: page.totalAttribute,
      });
    }

    relocateMessage(page);

    if (page.shadowHost) {
      styleShadowP(page.shadowHost, { fontSize: '0.8rem', fontWeight: '600' });
    }
  }

  function handleAdditional() {
    const flag = document.querySelector('div.price-list > square-placement');
    if (flag) flag.style.display = 'none';
  }

  /* ============================================================
   * OBSERVER MANAGER
   * ========================================================== */
  const ObserverManager = {
    _observers: [],
    register(target, callback) {
      if (!target) return null;
      const opts = { childList: true, subtree: true, attributes: true };
      const observer = new MutationObserver(() => {
        observer.disconnect();          // ignore our own mutations below
        try {
          callback();
        } finally {
          observer.observe(target, opts); // resume for genuine external changes
        }
      });
      observer.observe(target, opts);
      this._observers.push(observer);
      return observer;
    },
    disconnectAll() {
      this._observers.forEach((o) => o.disconnect());
      this._observers = [];
    },
  };

  /* ============================================================
   * INIT
   * ========================================================== */
  function init() {
    utils.injectStylesOnce(CONFIG.shared.styleTagId, WIDGET_CSS);

    const { pathname } = window.location;

    if (pathname.startsWith('/products/')) {
      ObserverManager.register(
        document.querySelector(CONFIG.observeTargets.productForm),
        handleProduct
      );
      ObserverManager.register(
        document.querySelector(CONFIG.observeTargets.priceList),
        handleAdditional
      );
    }

    if (pathname === '/cart' || pathname.startsWith('/cart/')) {
      ObserverManager.register(
        document.querySelector(CONFIG.observeTargets.cartAside),
        handleCart
      );
    }
  }

  document.addEventListener('DOMContentLoaded', init);
  window.addEventListener('beforeunload', () => ObserverManager.disconnectAll());
})();